import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

import { Platform } from '../enums/platform';
import { ConsoleResourceType } from '../enums/console-resource-type';
import { QuerySuggestionResource } from '../enums/query-suggestion-resource';
import { ProjectEmailTemplateId } from '../enums/project-email-template-id';
import { ProjectEmailTemplateLocale } from '../enums/project-email-template-locale';
export class Console {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Receive the details of a campaign using its ID.
     *
     * @param {string} params.campaignId - ID of the campaign
     * @throws {AppwriteException}
     * @returns {Promise<Models.Campaign>}
     */
    getCampaign(params: { campaignId: string }): Promise<Models.Campaign>;
    /**
     * Receive the details of a campaign using its ID.
     *
     * @param {string} campaignId - ID of the campaign
     * @throws {AppwriteException}
     * @returns {Promise<Models.Campaign>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getCampaign(campaignId: string): Promise<Models.Campaign>;
    getCampaign(
        paramsOrFirst: { campaignId: string } | string,
    ): Promise<Models.Campaign> {
        let params: { campaignId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { campaignId: string };
        } else {
            params = {
                campaignId: paramsOrFirst as string,
            };
        }

        const campaignId = params.campaignId;

        if (typeof campaignId === 'undefined' || campaignId === '') {
            throw new AppwriteException(
                'Missing required parameter: "campaignId"',
            );
        }
        const apiPath = '/console/campaigns/{campaignId}'.replace(
            '{campaignId}',
            encodeURIComponent(String(campaignId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'campaign',
            models: {
                campaign: {
                    defaults: {
                        $id: '',
                        template: '',
                        title: '',
                        description: '',
                    },
                    properties: {
                        reviews: {
                            items: 'review',
                        },
                    },
                },
                review: {
                    defaults: {
                        name: '',
                        image: '',
                        description: '',
                        review: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the details of a coupon using it's coupon ID.
     *
     * @param {string} params.couponId - ID of the coupon
     * @throws {AppwriteException}
     * @returns {Promise<Models.Coupon>}
     */
    getCoupon(params: { couponId: string }): Promise<Models.Coupon>;
    /**
     * Get the details of a coupon using it's coupon ID.
     *
     * @param {string} couponId - ID of the coupon
     * @throws {AppwriteException}
     * @returns {Promise<Models.Coupon>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getCoupon(couponId: string): Promise<Models.Coupon>;
    getCoupon(
        paramsOrFirst: { couponId: string } | string,
    ): Promise<Models.Coupon> {
        let params: { couponId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { couponId: string };
        } else {
            params = {
                couponId: paramsOrFirst as string,
            };
        }

        const couponId = params.couponId;

        if (typeof couponId === 'undefined' || couponId === '') {
            throw new AppwriteException(
                'Missing required parameter: "couponId"',
            );
        }
        const apiPath = '/console/coupons/{couponId}'.replace(
            '{couponId}',
            encodeURIComponent(String(couponId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'coupon',
            models: {
                coupon: {
                    defaults: {
                        $id: '',
                        code: '',
                        credits: 0,
                        expiration: '',
                        campaign: '',
                        status: '',
                        onlyNewOrgs: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a list of all the project's databases. You can use the query params to filter your results. This returns every database across all types and product APIs in a single call.
     *
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: name
     * @param {string} params.search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DatabaseList>}
     */
    listDatabases(params?: {
        queries?: string[];
        search?: string;
        total?: boolean;
    }): Promise<Models.DatabaseList>;
    /**
     * Get a list of all the project's databases. You can use the query params to filter your results. This returns every database across all types and product APIs in a single call.
     *
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: name
     * @param {string} search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DatabaseList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listDatabases(
        queries?: string[],
        search?: string,
        total?: boolean,
    ): Promise<Models.DatabaseList>;
    listDatabases(
        paramsOrFirst?:
            { queries?: string[]; search?: string; total?: boolean } | string[],
        ...rest: [string?, boolean?]
    ): Promise<Models.DatabaseList> {
        let params: { queries?: string[]; search?: string; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                search?: string;
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                search: rest[0] as string,
                total: rest[1] as boolean,
            };
        }

        const queries = params.queries;
        const search = params.search;
        const total = params.total;

        const apiPath = '/console/databases';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof search !== 'undefined') {
            apiPayload['search'] = search;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'databaseList',
            models: {
                databaseList: {
                    defaults: {
                        total: 0,
                        databases: [],
                    },
                    properties: {
                        databases: {
                            items: 'database',
                        },
                    },
                },
                database: {
                    defaults: {
                        $id: '',
                        name: '',
                        description: '',
                        $createdAt: '',
                        $updatedAt: '',
                        enabled: true,
                        type: 'legacy',
                    },
                    properties: {
                        policies: {
                            items: 'backupPolicy',
                        },
                        archives: {
                            items: 'backupArchive',
                        },
                    },
                },
                backupPolicy: {
                    defaults: {
                        $id: '',
                        name: '',
                        $createdAt: '',
                        $updatedAt: '',
                        services: [],
                        resources: [],
                        retention: 1,
                        schedule: '0 * * * *',
                        type: 'full',
                        enabled: false,
                    },
                },
                backupArchive: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        policyId: '',
                        status: '',
                        migrationId: '',
                        services: [],
                        resources: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List all OAuth2 providers supported by the Appwrite server, along with the parameters required to configure each provider. The response excludes mock providers but includes sandbox providers.
     *
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ConsoleOAuth2ProviderList>}
     */
    listOAuth2Providers(params?: {
        total?: boolean;
    }): Promise<Models.ConsoleOAuth2ProviderList>;
    /**
     * List all OAuth2 providers supported by the Appwrite server, along with the parameters required to configure each provider. The response excludes mock providers but includes sandbox providers.
     *
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ConsoleOAuth2ProviderList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listOAuth2Providers(
        total?: boolean,
    ): Promise<Models.ConsoleOAuth2ProviderList>;
    listOAuth2Providers(
        paramsOrFirst?: { total?: boolean } | boolean,
    ): Promise<Models.ConsoleOAuth2ProviderList> {
        let params: { total?: boolean };

        if (
            typeof paramsOrFirst === 'undefined' ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as { total?: boolean };
        } else {
            params = {
                total: paramsOrFirst as boolean,
            };
        }

        const total = params.total;

        const apiPath = '/console/oauth2-providers';
        const apiPayload: Payload = {};
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'consoleOAuth2ProviderList',
            models: {
                consoleOAuth2ProviderList: {
                    defaults: {
                        total: 0,
                        oAuth2Providers: [],
                    },
                    properties: {
                        oAuth2Providers: {
                            items: 'consoleOAuth2Provider',
                        },
                    },
                },
                consoleOAuth2Provider: {
                    defaults: {
                        $id: '',
                        parameters: [],
                    },
                    properties: {
                        parameters: {
                            items: 'consoleOAuth2ProviderParameter',
                        },
                    },
                },
                consoleOAuth2ProviderParameter: {
                    defaults: {
                        $id: '',
                        name: '',
                        example: '',
                        hint: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Return a list of all available plans.
     *
     * @param {Platform} params.platform - Platform type
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingPlanList>}
     */
    getPlans(params?: { platform?: Platform }): Promise<Models.BillingPlanList>;
    /**
     * Return a list of all available plans.
     *
     * @param {Platform} platform - Platform type
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingPlanList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPlans(platform?: Platform): Promise<Models.BillingPlanList>;
    getPlans(
        paramsOrFirst?: { platform?: Platform } | Platform,
    ): Promise<Models.BillingPlanList> {
        let params: { platform?: Platform };

        if (
            typeof paramsOrFirst === 'undefined' ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst) &&
                'platform' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { platform?: Platform };
        } else {
            params = {
                platform: paramsOrFirst as Platform,
            };
        }

        const platform = params.platform;

        const apiPath = '/console/plans';
        const apiPayload: Payload = {};
        if (typeof platform !== 'undefined') {
            apiPayload['platform'] = platform;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'billingPlanList',
            models: {
                billingPlanList: {
                    defaults: {
                        total: 0,
                        plans: [],
                    },
                    properties: {
                        plans: {
                            items: 'billingPlan',
                        },
                    },
                },
                billingPlan: {
                    defaults: {
                        $id: '',
                        name: '',
                        desc: '',
                        order: 0,
                        price: 0,
                        trial: 0,
                        bandwidth: 0,
                        storage: 0,
                        imageTransformations: 0,
                        screenshotsGenerated: 0,
                        webhooks: 0,
                        wafRules: 0,
                        environments: 0,
                        projects: 0,
                        platforms: 0,
                        users: 0,
                        teams: 0,
                        databases: 0,
                        databasesReads: 0,
                        databasesWrites: 0,
                        databasesBatchSize: 0,
                        buckets: 0,
                        fileSize: 0,
                        functions: 0,
                        sites: 0,
                        executions: 0,
                        executionsRetentionCount: 1000,
                        GBHours: 0,
                        realtime: 0,
                        realtimeMessages: 0,
                        messages: 0,
                        topics: 0,
                        authPhone: 0,
                        domains: 0,
                        usageLogs: 0,
                        projectInactivityDays: 0,
                        alertLimit: 80,
                        budgetCapEnabled: false,
                        customSmtp: false,
                        emailBranding: false,
                        requiresPaymentMethod: true,
                        requiresBillingAddress: false,
                        isAvailable: true,
                        selfService: true,
                        premiumSupport: true,
                        budgeting: true,
                        supportsMockNumbers: true,
                        supportsOrganizationRoles: true,
                        supportsCredits: false,
                        supportsDedicatedDatabases: false,
                        supportsDisposableEmailValidation: false,
                        supportsCanonicalEmailValidation: false,
                        supportsFreeEmailValidation: false,
                        supportsCorporateEmailValidation: false,
                        supportsProjectSpecificRoles: false,
                        usagePerProject: true,
                        deploymentSize: 0,
                        buildSize: 0,
                        functionsIntervalMinimum: 0,
                        functionsAllowCron: true,
                        databasesAllowEncrypt: false,
                        databaseComputeCredit: 0,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the details of a plan using its plan ID.
     *
     * @param {string} params.planId - Plan id
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingPlan>}
     */
    getPlan(params: { planId: string }): Promise<Models.BillingPlan>;
    /**
     * Get the details of a plan using its plan ID.
     *
     * @param {string} planId - Plan id
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingPlan>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPlan(planId: string): Promise<Models.BillingPlan>;
    getPlan(
        paramsOrFirst: { planId: string } | string,
    ): Promise<Models.BillingPlan> {
        let params: { planId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { planId: string };
        } else {
            params = {
                planId: paramsOrFirst as string,
            };
        }

        const planId = params.planId;

        if (typeof planId === 'undefined' || planId === '') {
            throw new AppwriteException('Missing required parameter: "planId"');
        }
        const apiPath = '/console/plans/{planId}'.replace(
            '{planId}',
            encodeURIComponent(String(planId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'billingPlan',
            models: {
                billingPlan: {
                    defaults: {
                        $id: '',
                        name: '',
                        desc: '',
                        order: 0,
                        price: 0,
                        trial: 0,
                        bandwidth: 0,
                        storage: 0,
                        imageTransformations: 0,
                        screenshotsGenerated: 0,
                        webhooks: 0,
                        wafRules: 0,
                        environments: 0,
                        projects: 0,
                        platforms: 0,
                        users: 0,
                        teams: 0,
                        databases: 0,
                        databasesReads: 0,
                        databasesWrites: 0,
                        databasesBatchSize: 0,
                        buckets: 0,
                        fileSize: 0,
                        functions: 0,
                        sites: 0,
                        executions: 0,
                        executionsRetentionCount: 1000,
                        GBHours: 0,
                        realtime: 0,
                        realtimeMessages: 0,
                        messages: 0,
                        topics: 0,
                        authPhone: 0,
                        domains: 0,
                        usageLogs: 0,
                        projectInactivityDays: 0,
                        alertLimit: 80,
                        budgetCapEnabled: false,
                        customSmtp: false,
                        emailBranding: false,
                        requiresPaymentMethod: true,
                        requiresBillingAddress: false,
                        isAvailable: true,
                        selfService: true,
                        premiumSupport: true,
                        budgeting: true,
                        supportsMockNumbers: true,
                        supportsOrganizationRoles: true,
                        supportsCredits: false,
                        supportsDedicatedDatabases: false,
                        supportsDisposableEmailValidation: false,
                        supportsCanonicalEmailValidation: false,
                        supportsFreeEmailValidation: false,
                        supportsCorporateEmailValidation: false,
                        supportsProjectSpecificRoles: false,
                        usagePerProject: true,
                        deploymentSize: 0,
                        buildSize: 0,
                        functionsIntervalMinimum: 0,
                        functionsAllowCron: true,
                        databasesAllowEncrypt: false,
                        databaseComputeCredit: 0,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the catalog of Postgres extensions that can be installed on a dedicated Postgres database.
     *
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.PostgresExtensionList>}
     */
    listPostgresExtensions(): Promise<Models.PostgresExtensionList> {
        const apiPath = '/console/postgres-extensions';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'postgresExtensionList',
            models: {
                postgresExtensionList: {
                    defaults: {
                        total: 0,
                        extensions: [],
                    },
                    properties: {
                        extensions: {
                            items: 'postgresExtension',
                        },
                    },
                },
                postgresExtension: {
                    defaults: {
                        key: '',
                        name: '',
                        description: '',
                        category: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Receive the details of a program using its ID.
     *
     * @param {string} params.programId - ID of the program
     * @throws {AppwriteException}
     * @returns {Promise<Models.Program>}
     */
    getProgram(params: { programId: string }): Promise<Models.Program>;
    /**
     * Receive the details of a program using its ID.
     *
     * @param {string} programId - ID of the program
     * @throws {AppwriteException}
     * @returns {Promise<Models.Program>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getProgram(programId: string): Promise<Models.Program>;
    getProgram(
        paramsOrFirst: { programId: string } | string,
    ): Promise<Models.Program> {
        let params: { programId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { programId: string };
        } else {
            params = {
                programId: paramsOrFirst as string,
            };
        }

        const programId = params.programId;

        if (typeof programId === 'undefined' || programId === '') {
            throw new AppwriteException(
                'Missing required parameter: "programId"',
            );
        }
        const apiPath = '/console/programs/{programId}'.replace(
            '{programId}',
            encodeURIComponent(String(programId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'program',
            models: {
                program: {
                    defaults: {
                        $id: '',
                        title: '',
                        description: '',
                        tag: '',
                        icon: '',
                        url: '',
                        active: true,
                        external: true,
                        billingPlanId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new membership for an account to a program.
     *
     * @param {string} params.programId - ID of the program
     * @throws {AppwriteException}
     * @returns {Promise<Models.Organization<Preferences>>}
     */
    createProgramMembership<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: { programId: string }): Promise<Models.Organization<Preferences>>;
    /**
     * Create a new membership for an account to a program.
     *
     * @param {string} programId - ID of the program
     * @throws {AppwriteException}
     * @returns {Promise<Models.Organization<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createProgramMembership<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(programId: string): Promise<Models.Organization<Preferences>>;
    createProgramMembership<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { programId: string } | string,
    ): Promise<Models.Organization<Preferences>> {
        let params: { programId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { programId: string };
        } else {
            params = {
                programId: paramsOrFirst as string,
            };
        }

        const programId = params.programId;

        if (typeof programId === 'undefined' || programId === '') {
            throw new AppwriteException(
                'Missing required parameter: "programId"',
            );
        }
        const apiPath = '/console/programs/{programId}/memberships'.replace(
            '{programId}',
            encodeURIComponent(String(programId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'organization',
            models: {
                organization: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        total: 0,
                        budgetAlerts: [75],
                        billingPlan: 'tier-0',
                        billingPlanId: 'tier-0',
                        billingEmail: '',
                        status: 'draft',
                        markedForDeletion: false,
                        platform: 'appwrite',
                        projects: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get all available regions for the console.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.ConsoleRegionList>}
     */
    listRegions(): Promise<Models.ConsoleRegionList> {
        const apiPath = '/console/regions';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'consoleRegionList',
            models: {
                consoleRegionList: {
                    defaults: {
                        total: 0,
                        regions: [],
                    },
                    properties: {
                        regions: {
                            items: 'consoleRegion',
                        },
                    },
                },
                consoleRegion: {
                    defaults: {
                        $id: '',
                        name: '',
                        disabled: false,
                        default: false,
                        flag: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Check if a resource ID is available.
     *
     * @param {string} params.value - Resource value.
     * @param {ConsoleResourceType} params.type - Resource type.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    getResource(params: {
        value: string;
        type: ConsoleResourceType;
    }): Promise<{}>;
    /**
     * Check if a resource ID is available.
     *
     * @param {string} value - Resource value.
     * @param {ConsoleResourceType} type - Resource type.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getResource(value: string, type: ConsoleResourceType): Promise<{}>;
    getResource(
        paramsOrFirst: { value: string; type: ConsoleResourceType } | string,
        ...rest: [ConsoleResourceType?]
    ): Promise<{}> {
        let params: { value: string; type: ConsoleResourceType };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                value: string;
                type: ConsoleResourceType;
            };
        } else {
            params = {
                value: paramsOrFirst as string,
                type: rest[0] as ConsoleResourceType,
            };
        }

        const value = params.value;
        const type = params.type;

        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        const apiPath = '/console/resources';
        const apiPayload: Payload = {};
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof type !== 'undefined') {
            apiPayload['type'] = type;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * List all scopes available for organization API keys, along with a description for each scope.
     *
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ConsoleKeyScopeList>}
     */
    listOrganizationScopes(params?: {
        total?: boolean;
    }): Promise<Models.ConsoleKeyScopeList>;
    /**
     * List all scopes available for organization API keys, along with a description for each scope.
     *
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ConsoleKeyScopeList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listOrganizationScopes(
        total?: boolean,
    ): Promise<Models.ConsoleKeyScopeList>;
    listOrganizationScopes(
        paramsOrFirst?: { total?: boolean } | boolean,
    ): Promise<Models.ConsoleKeyScopeList> {
        let params: { total?: boolean };

        if (
            typeof paramsOrFirst === 'undefined' ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as { total?: boolean };
        } else {
            params = {
                total: paramsOrFirst as boolean,
            };
        }

        const total = params.total;

        const apiPath = '/console/scopes/organization';
        const apiPayload: Payload = {};
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'consoleKeyScopeList',
            models: {
                consoleKeyScopeList: {
                    defaults: {
                        total: 0,
                        scopes: [],
                    },
                    properties: {
                        scopes: {
                            items: 'consoleKeyScope',
                        },
                    },
                },
                consoleKeyScope: {
                    defaults: {
                        $id: '',
                        description: '',
                        category: '',
                        deprecated: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List all scopes available for project API keys, along with a description for each scope.
     *
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ConsoleKeyScopeList>}
     */
    listProjectScopes(params?: {
        total?: boolean;
    }): Promise<Models.ConsoleKeyScopeList>;
    /**
     * List all scopes available for project API keys, along with a description for each scope.
     *
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ConsoleKeyScopeList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listProjectScopes(total?: boolean): Promise<Models.ConsoleKeyScopeList>;
    listProjectScopes(
        paramsOrFirst?: { total?: boolean } | boolean,
    ): Promise<Models.ConsoleKeyScopeList> {
        let params: { total?: boolean };

        if (
            typeof paramsOrFirst === 'undefined' ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as { total?: boolean };
        } else {
            params = {
                total: paramsOrFirst as boolean,
            };
        }

        const total = params.total;

        const apiPath = '/console/scopes/project';
        const apiPayload: Payload = {};
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'consoleKeyScopeList',
            models: {
                consoleKeyScopeList: {
                    defaults: {
                        total: 0,
                        scopes: [],
                    },
                    properties: {
                        scopes: {
                            items: 'consoleKeyScope',
                        },
                    },
                },
                consoleKeyScope: {
                    defaults: {
                        $id: '',
                        description: '',
                        category: '',
                        deprecated: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new source.
     *
     * @param {string | null} params.ref - Ref param
     * @param {string | null} params.referrer - Referrer
     * @param {string | null} params.utmSource - Utm source
     * @param {string | null} params.utmCampaign - Utm campaign
     * @param {string | null} params.utmMedium - Utm medium
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    createSource(params?: {
        ref?: string | null;
        referrer?: string | null;
        utmSource?: string | null;
        utmCampaign?: string | null;
        utmMedium?: string | null;
    }): Promise<{}>;
    /**
     * Create a new source.
     *
     * @param {string | null} ref - Ref param
     * @param {string | null} referrer - Referrer
     * @param {string | null} utmSource - Utm source
     * @param {string | null} utmCampaign - Utm campaign
     * @param {string | null} utmMedium - Utm medium
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createSource(
        ref?: string | null,
        referrer?: string | null,
        utmSource?: string | null,
        utmCampaign?: string | null,
        utmMedium?: string | null,
    ): Promise<{}>;
    createSource(
        paramsOrFirst?:
            | {
                  ref?: string | null;
                  referrer?: string | null;
                  utmSource?: string | null;
                  utmCampaign?: string | null;
                  utmMedium?: string | null;
              }
            | string
            | null,
        ...rest: [
            (string | null)?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
        ]
    ): Promise<{}> {
        let params: {
            ref?: string | null;
            referrer?: string | null;
            utmSource?: string | null;
            utmCampaign?: string | null;
            utmMedium?: string | null;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                ref?: string | null;
                referrer?: string | null;
                utmSource?: string | null;
                utmCampaign?: string | null;
                utmMedium?: string | null;
            };
        } else {
            params = {
                ref: paramsOrFirst as string | null,
                referrer: rest[0] as string | null,
                utmSource: rest[1] as string | null,
                utmCampaign: rest[2] as string | null,
                utmMedium: rest[3] as string | null,
            };
        }

        const ref = params.ref;
        const referrer = params.referrer;
        const utmSource = params.utmSource;
        const utmCampaign = params.utmCampaign;
        const utmMedium = params.utmMedium;

        const apiPath = '/console/sources';
        const apiPayload: Payload = {};
        if (typeof ref !== 'undefined') {
            apiPayload['ref'] = ref;
        }
        if (typeof referrer !== 'undefined') {
            apiPayload['referrer'] = referrer;
        }
        if (typeof utmSource !== 'undefined') {
            apiPayload['utmSource'] = utmSource;
        }
        if (typeof utmCampaign !== 'undefined') {
            apiPayload['utmCampaign'] = utmCampaign;
        }
        if (typeof utmMedium !== 'undefined') {
            apiPayload['utmMedium'] = utmMedium;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Suggests column names and their size limits based on the provided table name. The API will also analyze other tables in the same database to provide context-aware suggestions, ensuring consistency across schema design. Users may optionally provide custom context to further refine the suggestions.
     *
     * @param {string} params.databaseId - Database ID.
     * @param {string} params.tableId - Table ID.
     * @param {string} params.context - Optional user provided context to refine suggestions.
     * @param {number} params.min - Minimum number of suggestions to generate.
     * @param {number} params.max - Maximum number of suggestions to generate.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ColumnList>}
     */
    suggestColumns(params: {
        databaseId: string;
        tableId: string;
        context?: string;
        min?: number;
        max?: number;
    }): Promise<Models.ColumnList>;
    /**
     * Suggests column names and their size limits based on the provided table name. The API will also analyze other tables in the same database to provide context-aware suggestions, ensuring consistency across schema design. Users may optionally provide custom context to further refine the suggestions.
     *
     * @param {string} databaseId - Database ID.
     * @param {string} tableId - Table ID.
     * @param {string} context - Optional user provided context to refine suggestions.
     * @param {number} min - Minimum number of suggestions to generate.
     * @param {number} max - Maximum number of suggestions to generate.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ColumnList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    suggestColumns(
        databaseId: string,
        tableId: string,
        context?: string,
        min?: number,
        max?: number,
    ): Promise<Models.ColumnList>;
    suggestColumns(
        paramsOrFirst:
            | {
                  databaseId: string;
                  tableId: string;
                  context?: string;
                  min?: number;
                  max?: number;
              }
            | string,
        ...rest: [string?, string?, number?, number?]
    ): Promise<Models.ColumnList> {
        let params: {
            databaseId: string;
            tableId: string;
            context?: string;
            min?: number;
            max?: number;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                databaseId: string;
                tableId: string;
                context?: string;
                min?: number;
                max?: number;
            };
        } else {
            params = {
                databaseId: paramsOrFirst as string,
                tableId: rest[0] as string,
                context: rest[1] as string,
                min: rest[2] as number,
                max: rest[3] as number,
            };
        }

        const databaseId = params.databaseId;
        const tableId = params.tableId;
        const context = params.context;
        const min = params.min;
        const max = params.max;

        if (typeof databaseId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "databaseId"',
            );
        }
        if (typeof tableId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "tableId"',
            );
        }
        const apiPath = '/console/suggestions/columns';
        const apiPayload: Payload = {};
        if (typeof databaseId !== 'undefined') {
            apiPayload['databaseId'] = databaseId;
        }
        if (typeof tableId !== 'undefined') {
            apiPayload['tableId'] = tableId;
        }
        if (typeof context !== 'undefined') {
            apiPayload['context'] = context;
        }
        if (typeof min !== 'undefined') {
            apiPayload['min'] = min;
        }
        if (typeof max !== 'undefined') {
            apiPayload['max'] = max;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'columnList',
            models: {
                columnList: {
                    defaults: {
                        total: 0,
                        columns: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Suggests database indexes for table columns based on the provided table structure and existing columns. The API will also analyze the table's column types, names, and patterns to recommend optimal indexes that improve query performance for common database operations like filtering, sorting, and searching.
     *
     * @param {string} params.databaseId - Database ID.
     * @param {string} params.tableId - Table ID.
     * @param {number} params.min - Minimum number of suggestions to generate.
     * @param {number} params.max - Maximum number of suggestions to generate.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ColumnIndexList>}
     */
    suggestIndexes(params: {
        databaseId: string;
        tableId: string;
        min?: number;
        max?: number;
    }): Promise<Models.ColumnIndexList>;
    /**
     * Suggests database indexes for table columns based on the provided table structure and existing columns. The API will also analyze the table's column types, names, and patterns to recommend optimal indexes that improve query performance for common database operations like filtering, sorting, and searching.
     *
     * @param {string} databaseId - Database ID.
     * @param {string} tableId - Table ID.
     * @param {number} min - Minimum number of suggestions to generate.
     * @param {number} max - Maximum number of suggestions to generate.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ColumnIndexList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    suggestIndexes(
        databaseId: string,
        tableId: string,
        min?: number,
        max?: number,
    ): Promise<Models.ColumnIndexList>;
    suggestIndexes(
        paramsOrFirst:
            | {
                  databaseId: string;
                  tableId: string;
                  min?: number;
                  max?: number;
              }
            | string,
        ...rest: [string?, number?, number?]
    ): Promise<Models.ColumnIndexList> {
        let params: {
            databaseId: string;
            tableId: string;
            min?: number;
            max?: number;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                databaseId: string;
                tableId: string;
                min?: number;
                max?: number;
            };
        } else {
            params = {
                databaseId: paramsOrFirst as string,
                tableId: rest[0] as string,
                min: rest[1] as number,
                max: rest[2] as number,
            };
        }

        const databaseId = params.databaseId;
        const tableId = params.tableId;
        const min = params.min;
        const max = params.max;

        if (typeof databaseId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "databaseId"',
            );
        }
        if (typeof tableId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "tableId"',
            );
        }
        const apiPath = '/console/suggestions/indexes';
        const apiPayload: Payload = {};
        if (typeof databaseId !== 'undefined') {
            apiPayload['databaseId'] = databaseId;
        }
        if (typeof tableId !== 'undefined') {
            apiPayload['tableId'] = tableId;
        }
        if (typeof min !== 'undefined') {
            apiPayload['min'] = min;
        }
        if (typeof max !== 'undefined') {
            apiPayload['max'] = max;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'columnIndexList',
            models: {
                columnIndexList: {
                    defaults: {
                        total: 0,
                        indexes: [],
                    },
                    properties: {
                        indexes: {
                            items: 'columnIndex',
                        },
                    },
                },
                columnIndex: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        type: '',
                        status: '',
                        error: '',
                        columns: [],
                        lengths: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Suggest valid Appwrite query JSON objects for a supported list resource from free-text user intent. The endpoint picks a validator based on `resource` — for system resources it uses the static validator and its allowed attributes, and for user-owned table rows it loads the table schema and validates against those attributes at request time. The returned queries are guaranteed to parse and pass the relevant queries validator.
     *
     *
     * @param {QuerySuggestionResource} params.resource - Resource to generate queries for.
     * @param {string} params.input - Natural language query intent used to generate filters/sorting/pagination.
     * @param {string} params.databaseId - Database ID. Required when resource is `tables` or `rows`.
     * @param {string} params.tableId - Table ID. Required when resource is `rows`.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    suggestQueries(params: {
        resource: QuerySuggestionResource;
        input: string;
        databaseId?: string;
        tableId?: string;
    }): Promise<{}>;
    /**
     * Suggest valid Appwrite query JSON objects for a supported list resource from free-text user intent. The endpoint picks a validator based on `resource` — for system resources it uses the static validator and its allowed attributes, and for user-owned table rows it loads the table schema and validates against those attributes at request time. The returned queries are guaranteed to parse and pass the relevant queries validator.
     *
     *
     * @param {QuerySuggestionResource} resource - Resource to generate queries for.
     * @param {string} input - Natural language query intent used to generate filters/sorting/pagination.
     * @param {string} databaseId - Database ID. Required when resource is `tables` or `rows`.
     * @param {string} tableId - Table ID. Required when resource is `rows`.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    suggestQueries(
        resource: QuerySuggestionResource,
        input: string,
        databaseId?: string,
        tableId?: string,
    ): Promise<{}>;
    suggestQueries(
        paramsOrFirst:
            | {
                  resource: QuerySuggestionResource;
                  input: string;
                  databaseId?: string;
                  tableId?: string;
              }
            | QuerySuggestionResource,
        ...rest: [string?, string?, string?]
    ): Promise<{}> {
        let params: {
            resource: QuerySuggestionResource;
            input: string;
            databaseId?: string;
            tableId?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('resource' in paramsOrFirst ||
                'input' in paramsOrFirst ||
                'databaseId' in paramsOrFirst ||
                'tableId' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                resource: QuerySuggestionResource;
                input: string;
                databaseId?: string;
                tableId?: string;
            };
        } else {
            params = {
                resource: paramsOrFirst as QuerySuggestionResource,
                input: rest[0] as string,
                databaseId: rest[1] as string,
                tableId: rest[2] as string,
            };
        }

        const resource = params.resource;
        const input = params.input;
        const databaseId = params.databaseId;
        const tableId = params.tableId;

        if (typeof resource === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "resource"',
            );
        }
        if (typeof input === 'undefined') {
            throw new AppwriteException('Missing required parameter: "input"');
        }
        const apiPath = '/console/suggestions/queries';
        const apiPayload: Payload = {};
        if (typeof resource !== 'undefined') {
            apiPayload['resource'] = resource;
        }
        if (typeof input !== 'undefined') {
            apiPayload['input'] = input;
        }
        if (typeof databaseId !== 'undefined') {
            apiPayload['databaseId'] = databaseId;
        }
        if (typeof tableId !== 'undefined') {
            apiPayload['tableId'] = tableId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Get the Appwrite built-in default email template for the specified type and locale. Always returns the unmodified default, ignoring any custom project overrides.
     *
     * @param {ProjectEmailTemplateId} params.templateId - Email template type. Can be one of: verification, magicSession, recovery, invitation, mfaChallenge, sessionAlert, otpSession, otpVerification, otpRecovery
     * @param {ProjectEmailTemplateLocale} params.locale - Template locale. If left empty, the fallback locale (en) will be used.
     * @throws {AppwriteException}
     * @returns {Promise<Models.EmailTemplate>}
     */
    getEmailTemplate(params: {
        templateId: ProjectEmailTemplateId;
        locale?: ProjectEmailTemplateLocale;
    }): Promise<Models.EmailTemplate>;
    /**
     * Get the Appwrite built-in default email template for the specified type and locale. Always returns the unmodified default, ignoring any custom project overrides.
     *
     * @param {ProjectEmailTemplateId} templateId - Email template type. Can be one of: verification, magicSession, recovery, invitation, mfaChallenge, sessionAlert, otpSession, otpVerification, otpRecovery
     * @param {ProjectEmailTemplateLocale} locale - Template locale. If left empty, the fallback locale (en) will be used.
     * @throws {AppwriteException}
     * @returns {Promise<Models.EmailTemplate>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getEmailTemplate(
        templateId: ProjectEmailTemplateId,
        locale?: ProjectEmailTemplateLocale,
    ): Promise<Models.EmailTemplate>;
    getEmailTemplate(
        paramsOrFirst:
            | {
                  templateId: ProjectEmailTemplateId;
                  locale?: ProjectEmailTemplateLocale;
              }
            | ProjectEmailTemplateId,
        ...rest: [ProjectEmailTemplateLocale?]
    ): Promise<Models.EmailTemplate> {
        let params: {
            templateId: ProjectEmailTemplateId;
            locale?: ProjectEmailTemplateLocale;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('templateId' in paramsOrFirst || 'locale' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                templateId: ProjectEmailTemplateId;
                locale?: ProjectEmailTemplateLocale;
            };
        } else {
            params = {
                templateId: paramsOrFirst as ProjectEmailTemplateId,
                locale: rest[0] as ProjectEmailTemplateLocale,
            };
        }

        const templateId = params.templateId;
        const locale = params.locale;

        if (typeof templateId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "templateId"',
            );
        }
        const apiPath = '/console/templates/email/{templateId}'.replace(
            '{templateId}',
            encodeURIComponent(String(templateId)),
        );
        const apiPayload: Payload = {};
        if (typeof locale !== 'undefined') {
            apiPayload['locale'] = locale;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'emailTemplate',
            models: {
                emailTemplate: {
                    defaults: {
                        templateId: '',
                        locale: '',
                        message: '',
                        senderName: '',
                        senderEmail: '',
                        replyToEmail: '',
                        replyToName: '',
                        subject: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get all Environment Variables that are relevant for the console.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.ConsoleVariables>}
     */
    variables(): Promise<Models.ConsoleVariables> {
        const apiPath = '/console/variables';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'consoleVariables',
            models: {
                consoleVariables: {
                    defaults: {
                        _APP_DOMAIN_TARGET_CNAME: '',
                        _APP_DOMAIN_TARGET_A: '',
                        _APP_DOMAIN_TARGET_AAAA: '',
                        _APP_DOMAIN_TARGET_CAA: '',
                        _APP_USAGE_STATS: '',
                        _APP_VCS_ENABLED: false,
                        _APP_VCS_PROVIDERS: [],
                        _APP_VCS_PROVIDERS_WITH_REPOSITORY_CREATION: [],
                        _APP_VCS_PROVIDERS_WITH_PUBLIC_REPOSITORIES: [],
                        _APP_DOMAIN_ENABLED: false,
                        _APP_DOMAIN_SITES: '',
                        _APP_DOMAIN_FUNCTIONS: '',
                        _APP_OPTIONS_FORCE_HTTPS: '',
                        _APP_DOMAINS_NAMESERVERS: '',
                        _APP_DB_ADAPTER: 'mariadb',
                        supportForAttributes: true,
                        supportForRelationships: true,
                        supportForOperators: true,
                        supportForSpatials: true,
                        supportForAggregations: true,
                        supportForJoins: true,
                        supportForSpatialIndexNull: false,
                        supportForFulltextWildcard: true,
                        supportForMultipleFulltextIndexes: true,
                        supportForTrigramIndex: false,
                        supportForAttributeResizing: true,
                        supportForSchemas: true,
                        maxIndexLength: 768,
                        supportForIntegerIds: true,
                        _APP_CONSOLE_EMAIL_VERIFICATION: '',
                        _APP_DOMAIN_EMAIL: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }
}
